import { Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { ICellRendererParams } from '@ag-grid-community/core';
import { MatIconModule } from '@angular/material/icon';
import { NgIf } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';

import { UserService } from '../../../shared/services/user.service';
import { AdminService } from '../../../administration/admin.service';
import { OrganizationUserInfo } from '../../models/organization-user-info';
import { OrganizationService } from '../../services/organization.service';
import { TranslationService } from '../../../shared/services/translation.service';
import { DialogDeleteComponent } from '../../components/user-card/self/dialog-delete/dialog-delete.component';

interface ManageUserStatusCellModel extends ICellRendererParams<unknown, unknown> {
	organizationId: string;
	usersList: OrganizationUserInfo[];
	getOrganizationUsersList(): void;
}

@Component({
	selector: 'copo-manage-user-status',
	templateUrl: './manage-user-status.component.html',
	styleUrl: './manage-user-status.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [NgIf, MatIconModule]
})
export class ManageUserStatusComponent implements ICellRendererAngularComp, OnDestroy {
	params!: ManageUserStatusCellModel;

	dialogSubscription: Subscription = new Subscription();
	private removeUserSub: Subscription = Subscription.EMPTY;
	constructor(
		private readonly userService: UserService,
		private readonly adminService: AdminService,
		private readonly organizationService: OrganizationService,
		private readonly translationService: TranslationService,
		private readonly snackBar: MatSnackBar,
		readonly dialog: MatDialog,
	) {}

	ngOnDestroy() {
		this.removeUserSub.unsubscribe();
	}

	agInit(params: ManageUserStatusCellModel) {
		this.params = params;
	}

	refresh(params: ManageUserStatusCellModel): boolean {
		this.params = params;
		return true;
	}

	async accpetUser(params: ManageUserStatusCellModel) {
		const organizationUserInfo = <OrganizationUserInfo>params.data;
		const orgId = Number(this.params.organizationId);
		await this.adminService.updateMemberRole(organizationUserInfo.id, orgId).then(() => {
			this.params.getOrganizationUsersList();
		}).catch((err: unknown) => {
			console.warn('unable to update user', err);
		});
	}

	showDeletionDialog(params: ManageUserStatusCellModel) {
		const organizationUserInfo = <OrganizationUserInfo>params.data;
		if (params.node.id && params.data) {
			if (params.usersList.length === 1 && params.usersList[0].personMembers.role === 'dsu') {
				this.snackBar.open(
					this.translationService.getTR(
						'delete.only-administrator'
					),
					undefined,
					{
						duration: 5000,
					}
				);
			} else {
				const fullName = `${ organizationUserInfo.firstName } ${ organizationUserInfo.lastName }`;
				const role = organizationUserInfo.personMembers;
				const dialogRef = this.dialog.open(DialogDeleteComponent, {
					width: '60%',
					data: {
						title: fullName,
						userRole: role,
						usersList: params.usersList,
						userId: organizationUserInfo.id,
						orgId: Number(params.organizationId)
					}
				});

				this.dialogSubscription = dialogRef.afterClosed().subscribe(result => {
					if (result && params.data) {
						this.deleteUser(params);
					}
				});
			}
		}
	}

	deleteUser(params: ManageUserStatusCellModel) {
		const orgId = Number(this.params.organizationId);
		const organizationUserInfo = <OrganizationUserInfo>params.data;
		this.removeUserSub = this.organizationService.removeUser(orgId, organizationUserInfo.id).subscribe({
			next: () => {
				this.params.getOrganizationUsersList();
			},
			error: (err: unknown) => {
				console.error('error remove user', err);
			},
		});
	}

	isAdmin() {
		const loginUser = this.userService.loggedUser;
		if (loginUser) {
			return Boolean(loginUser.canUpdateOrganizationUsers(Number(this.params.organizationId)));
		}
		return false;
	}

	isAdminAndDeligationCheck(params: ManageUserStatusCellModel) {
		const organizationUserInfo = <OrganizationUserInfo>params.data;
		const loginUser = this.userService.loggedUser;
		return loginUser && this.isAdmin() && !loginUser.isDelegating()
			&& organizationUserInfo.personMembers.status === 'pend';
	}
}
